import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Carousel } from "antd";
import { useDispatch, useSelector } from "react-redux";

import {
  clearFeaturedStores,
  getFeaturedStores,
  selectFeaturedStores,
  setFeaturedStoresLoading
} from "../../routes/Store/store.ducks";
import FeaturedStoreCard from "./FeaturedStoreCard";
import styles from "./featuredStoresWidget.scss";
import ReactGA from "react-ga4";

const MIN_STORES_TO_SHOW = 10;
const SKELETON_COUNT = 6;

// Single source of truth for "will the widget render anything visible?".
// True during the initial skeleton phase as well as when real data passes the
// gate, so callers (e.g. ModulePage) can adjust surrounding spacing accordingly.
export const useFeaturedStoresWillRender = () => {
  const { items, totalCount, isLoading } = useSelector(selectFeaturedStores);
  if (isLoading && items.length === 0) return true;
  return totalCount >= MIN_STORES_TO_SHOW && items.length > 0;
};

interface Props {
  module?: string;
  category?: string;
  isMobile?: boolean;
}

const FeaturedStoresWidget = ({ module, category, isMobile }: Props) => {
  const dispatch = useDispatch();
  const { items, totalCount, isLoading } = useSelector(selectFeaturedStores);
  const carouselRef = useRef(null);
  const widgetRef = useRef(null);
  const currentSlide = useRef(0);

  const handleBeforeChange = useCallback((_, next) => {
    currentSlide.current = next;
  }, []);

  useEffect(() => {
    if (!module) return;
    dispatch(clearFeaturedStores());
    dispatch(setFeaturedStoresLoading(true));
    // @ts-ignore - thunk dispatch signature
    dispatch(getFeaturedStores(module, category));
    return () => {
      dispatch(clearFeaturedStores());
    };
  }, [module, category]);

  const carouselSettings = useMemo(
    () => ({
      dots: true,
      arrows: false,
      infinite: false,
      swipe: true,
      draggable: true,
      customPaging: () => (
        <div className={isMobile ? styles.dotMobile : styles.dot} />
      ),
      slidesToShow: isMobile ? 1 : 6,
      slidesToScroll: isMobile ? 1 : 6,
      responsive: [
        { breakpoint: 1600, settings: { slidesToShow: 5, slidesToScroll: 5 } },
        { breakpoint: 1280, settings: { slidesToShow: 4, slidesToScroll: 4 } },
        { breakpoint: 992, settings: { slidesToShow: 3, slidesToScroll: 3 } },
        { breakpoint: 768, settings: { slidesToShow: 2, slidesToScroll: 2 } },
        { breakpoint: 576, settings: { slidesToShow: 1, slidesToScroll: 1 } }
      ]
    }),
    [isMobile]
  );

  const shouldShowSkeleton = isLoading && items.length === 0
  const shouldShowContent = totalCount >= MIN_STORES_TO_SHOW && items.length > 0

  useEffect(() => {
    if (shouldShowSkeleton || !shouldShowContent) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          ReactGA.event({
            category: "Izdvojene trgovine naslovnica",
            action: "In Viewport",
            label: `Prikaz widgeta - ${category || "nepoznato"}`
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (widgetRef.current) {
      observer.observe(widgetRef.current);
    }

    return () => observer.disconnect();
  }, [shouldShowSkeleton, shouldShowContent, category])

  // Initial load — render skeleton so the page layout stays stable instead of
  // popping the widget in once data arrives.
  if (shouldShowSkeleton) {
    const skeletonSlides = Array.from({ length: SKELETON_COUNT });
    return (
      <div className={styles.widget}>
        <div className={styles.title}>Izdvojene trgovine</div>
        <div className={styles.carouselWrapper}>
          {/* @ts-ignore antd Carousel accepts slick-carousel responsive prop */}
          <Carousel {...carouselSettings}>
            {skeletonSlides.map((_, idx) => (
              <div key={`skeleton-${idx}`} className={styles.slide}>
                <div className={`${styles.card} ${styles.cardSkeleton}`}>
                  <div className={`${styles.skeletonLogo} ${styles.shimmer}`} />
                  <div className={`${styles.skeletonLine} ${styles.shimmer}`} />
                  <div className={`${styles.skeletonLineShort} ${styles.shimmer}`} />
                  <div className={`${styles.skeletonButton} ${styles.shimmer}`} />
                </div>
              </div>
            ))}
          </Carousel>
        </div>
      </div>
    );
  }

  if (!shouldShowContent) {
    return null;
  }

  return (
    <div ref={widgetRef} className={styles.widget}>
      <div className={styles.title}>Izdvojene trgovine</div>
      <div className={styles.carouselWrapper}>
        {/* @ts-ignore antd Carousel accepts slick-carousel responsive prop */}
        <Carousel
          ref={carouselRef}
          {...carouselSettings}
          beforeChange={handleBeforeChange}
          initialSlide={currentSlide.current}
        >
          {items.map(store => (
            <div key={store.userId} className={styles.slide}>
              <FeaturedStoreCard store={store} category={category} />
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default FeaturedStoresWidget;



// WEBPACK FOOTER //
// ./src/components/FeaturedStoresWidget/FeaturedStoresWidget.tsx